import { Avatar, Form } from 'antd';
import SvgIconCheck from '../../itemDetail/components/@svg/SvgCheck';
import { UserOutlined } from '@ant-design/icons';
import { SvgArrowDownSelect } from '../../../../../components/@svg/SvgArrowDownSelect';
import CustomSearchInput from '../../../../../components/input/CustomSearchInput';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Helper } from '../../../../../util/helper';

type CustomerLite = {
  id: string | number;
  userCode: string;
  name: string;
  avatarUrl?: string;
};

type CustomerIdSelectFieldProps = {
  form: any;
  fieldName?: string;
  customerOptions: CustomerLite[];
  placeholder?: string;
  inViewRefUser: (node?: Element | null) => void;
  handleSearch?: (v: any) => void;
};

export const CustomerIdSelectField: React.FC<CustomerIdSelectFieldProps> = ({
  form,
  fieldName = 'customerId',
  customerOptions,
  placeholder = '選択してください',
  inViewRefUser,
  handleSearch,
}) => {
  const inputRef = useRef<HTMLDivElement | null>(null);

  const selectedCustomerId = Form.useWatch(fieldName, form);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const errorMsg: string | undefined = form.getFieldError(fieldName)?.[0];
  const hasError = !!errorMsg;

  const selected = useMemo(() => {
    return customerOptions.find((x) => String(x.id) === String(selectedCustomerId));
  }, [customerOptions, selectedCustomerId]);

  const filteredList = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return customerOptions;

    return customerOptions.filter((x) => {
      const hay = `${x.userCode} ${x.name}`.toLowerCase();
      return hay.includes(q);
    });
  }, [customerOptions, keyword]);

  const onSelect = (v: CustomerLite) => {
    form.setFieldValue(fieldName, v.id);
    form.validateFields([fieldName]).catch(() => {});
    setOpen(false);
    handleSearch?.(v);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!open) return;
      const el = inputRef.current;
      if (el && !el.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) setKeyword('');
  }, [open]);

  return (
    <div className={`user-list ${hasError ? 'is-error' : ''}`} ref={inputRef}>
      <div className="user-list-input" onClick={() => setOpen((v) => !v)} role="button" tabIndex={0}>
        {selected ? (
          <div className="selected">
            <span className="value"> {selected.userCode}</span>
          </div>
        ) : (
          <span className="select">{placeholder}</span>
        )}
        <SvgArrowDownSelect />
      </div>

      {open && (
        <div className="user-list-select">
          <div className="select-search">
            <CustomSearchInput
              className="font-weight-500 font-size-16"
              placeholder="検索"
              value={keyword}
              onChange={({ target: { value } }) => setKeyword(value)}
              onFocus={() => setOpen(true)}
            />
          </div>

          <div className="list ebay__scroll-bar-custom">
            {filteredList.map((v, index) => {
              const nameChar = v.name ? Array.from(v.name)?.[0]?.toUpperCase() : null;
              const isLastItem = index === filteredList.length - 1;

              return (
                <div
                  key={`${v.id}-${index}`}
                  className="list-item"
                  onClick={() => onSelect(v)}
                  ref={isLastItem ? inViewRefUser : null}
                >
                  <Avatar
                    className="avatar"
                    src={Helper.getSourceFile(v.avatarUrl)}
                    size={32}
                    icon={nameChar ? <span className="none-avatar">{nameChar}</span> : <UserOutlined />}
                  />

                  <div className="detail">
                    <div className="user">
                      <span className="title">
                        <span>顧客ID</span>
                        <span>:</span>
                      </span>
                      <span className="value">{v.userCode}</span>
                    </div>

                    <div className="user">
                      <span className="title">
                        <span className="name">契約者名</span>
                        <span>:</span>
                      </span>
                      <span className="value-name">{v.name}</span>
                    </div>
                  </div>

                  {String(selectedCustomerId) === String(v.id) && <SvgIconCheck />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
